import { Role } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { useFieldState, useFieldApi } from 'informed';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useUser } from '@/hooks';

export const TierConfigFields = memo(function TierConfigFields() {
  const { t } = useLingui();
  const { user } = useUser();

  const { value: disableAllInboxUnread } = useFieldState(
    'tierConfig.disableAllInboxUnread',
  );

  const { setValueQuietly } = useFieldApi('tierConfig.disableAiInboxUnread');

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'name',
        label: t`Name`,
        required: true,
      },
      {
        name: 'url',
        label: t`URL`,
        uiProps: {
          placeholder: t`https://example.com`,
        },
      },
      {
        $relevant: {
          $when: () => user?.role === Role.SuperAdmin,
          $fields: [
            {
              name: 'tierConfig.pdfReaderEnabled',
              label: t`Luna PDF reader`,
              tooltip: t`Use Luna for new PDF processing and PDF links opened by agents. When off, use Mistral. Existing documents are unchanged until reprocessed.`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
            },
            {
              name: 'tierConfig.voiceBot',
              label: t`Voice bot`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
            },
          ],
        },
      },
      {
        name: 'tierConfig.disableAiInboxUnread',
        label: t`Disable AI inbox notifications`,
        tooltip: t`Hides unread badges for AI-mode threads. Operator handoff emails and live-chat notifications stay enabled.`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
        uiProps: {
          readOnly: disableAllInboxUnread,
        },
      },
      {
        name: 'tierConfig.disableAllInboxUnread',
        label: t`Disable all inbox notifications`,
        tooltip: t`Turns off inbox unread badges and operator handoff emails for this organization.`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
        uiProps: {
          onCheckedChange: () => {
            setValueQuietly(!disableAllInboxUnread);
          },
        },
      },
      {
        name: 'tierConfig.allowSharedData',
        label: t`Allow shared data sets between agents`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
      },
    ],
    [t, disableAllInboxUnread],
  );

  return (
    <Card title={t`Basic details`}>
      <SchemaFields schema={detailsSchema} />
    </Card>
  );
});
